import { Platform } from 'react-native';
import * as DocumentPicker from 'expo-document-picker';

/**
 * Cross-platform file picker + R2 upload helper for the attachments
 * flow (ADR-0060). Two surfaces:
 *
 *   - `pickAttachmentFile()` opens the native / web picker and
 *     returns the slim metadata the attachments router needs to
 *     issue a presigned URL (filename, mimeType, size) plus a
 *     platform-specific handle the upload step needs (web: a Blob;
 *     native: a `file://` URI).
 *
 *   - `uploadToR2(url, headers, file)` PUTs the bytes to the
 *     presigned URL. On web it `fetch`es with the Blob as body; on
 *     native it uses `expo-file-system`'s `uploadAsync` so we don't
 *     have to base64-round-trip large files through JS.
 *
 * Both surfaces are I/O thin so the hook layer can mock them without
 * pulling in DOM / FileSystem internals.
 */

/** MIME types the server accepts (mirror of `ALLOWED_ATTACHMENT_MIME_TYPES`).
 *  The literal-tuple type is what the tRPC input enum expects. */
export const ATTACHMENT_PICKER_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
  'message/rfc822',
] as const;

export type AttachmentMimeType = (typeof ATTACHMENT_PICKER_MIME_TYPES)[number];

export interface PickedAttachment {
  filename: string;
  mimeType: AttachmentMimeType;
  size: number;
  /** Web: a Blob ready to PUT. Native: undefined — use `uri`. */
  blob?: Blob | undefined;
  /** Native: a `file://` URI for `expo-file-system`. Web: undefined. */
  uri?: string | undefined;
}

function isAllowedMimeType(value: string): value is AttachmentMimeType {
  return (ATTACHMENT_PICKER_MIME_TYPES as readonly string[]).includes(value);
}

/**
 * Open the picker and return the picked attachment, or null when
 * the user cancelled. Throws when the picked file is rejected
 * client-side (wrong type, no readable size).
 */
export async function pickAttachmentFile(): Promise<PickedAttachment | null> {
  const result = await DocumentPicker.getDocumentAsync({
    type: [...ATTACHMENT_PICKER_MIME_TYPES],
    multiple: false,
    copyToCacheDirectory: true,
  });
  if (result.canceled || !result.assets.length) return null;
  const asset = result.assets[0];

  const filename = asset!.name;
  const mimeType = asset!.mimeType ?? '';
  const size = typeof asset!.size === 'number' ? asset!.size : 0;
  if (!mimeType) {
    throw new Error('Could not determine file type — try a PDF or image.');
  }
  if (!isAllowedMimeType(mimeType)) {
    throw new Error(`File type "${mimeType}" is not supported. Try a PDF or image.`);
  }
  if (!size || size <= 0) {
    throw new Error('Picked file has no readable size.');
  }

  if (Platform.OS === 'web') {
    // expo-document-picker on web wraps the File via blob URL or
    // gives us the `file` directly — we need a Blob for fetch().
    // The supported way is to refetch the asset URI (data: or blob:)
    // and read the response as a Blob.
    const fetched = await fetch(asset!.uri);
    const blob = await fetched.blob();
    return { filename, mimeType, size, blob };
  }
  return { filename, mimeType, size, uri: asset!.uri };
}

/**
 * PUT the picked file's bytes to the presigned R2 URL. Throws on
 * non-2xx status; returns void on success.
 *
 * Web path: `fetch(url, { method: 'PUT', body: Blob, headers })`.
 * Native path: `FileSystem.uploadAsync(url, uri, { httpMethod: 'PUT',
 *   uploadType: BINARY_CONTENT, headers })` so the bytes stream from
 *   the file system without a JS-side base64 round-trip.
 */
export async function uploadToR2(
  uploadUrl: string,
  headers: Record<string, string>,
  file: PickedAttachment
): Promise<void> {
  if (Platform.OS === 'web') {
    if (!file.blob) throw new Error('Web upload requires a Blob body');
    const res = await fetch(uploadUrl, { method: 'PUT', body: file.blob, headers });
    if (!res.ok) {
      throw new Error(`R2 upload failed (${res.status} ${res.statusText})`);
    }
    return;
  }
  if (!file.uri) throw new Error('Native upload requires a file URI');
  // Lazy import — pulls in native module bindings only when needed.
  const FileSystem = await import('expo-file-system/legacy');
  const result = await FileSystem.uploadAsync(uploadUrl, file.uri, {
    httpMethod: 'PUT',
    uploadType: FileSystem.FileSystemUploadType.BINARY_CONTENT,
    headers,
  });
  if (result.status < 200 || result.status >= 300) {
    throw new Error(`R2 upload failed (${result.status})`);
  }
}
